/**
 * 文件的上传下载流程服务
 * @author sunzhiguang
 * @date 2020/7/2
 */

import { FocusSDK, isFocusEnv, getAppSecretKey, getWebEnvObj } from "@/utils";
import logger from "@/utils/logger";
import { getAppVersion } from "@/utils/getVersion";
import UploadFileService, { TaskOptions } from "@jd/jdee-fs-desktop";
import Task from "@jd/jdee-fs-desktop/lib/es/Task";
import AppRuntimeEnv from "@/types/AppRuntimeEnv";
import { AuthData } from "@/types/LocalConfig";

const FileService: {
  uploadService: UploadFileService | null;
  downloadService: null;
} = {
  uploadService: null,
  downloadService: null
};

const FileTaskMap = new Map<string, Task[]>();

/**
 * 处始化文件服务
 */
export const initFileUploadService = async (appRuntimeEnv: AppRuntimeEnv, info: AuthData) => {
  const { appKey: key, hostOrigin } = appRuntimeEnv.fileService;
  let appSecretKey = "";
  if (FocusSDK.getDeviceInfo().platform === "web") {
    appSecretKey = getWebEnvObj().REACT_APP_WEB_SECRETKEY || "";
  } else {
    appSecretKey = getAppSecretKey(appRuntimeEnv.runtimeConfig.key);
  }
  const appKey = key || getWebEnvObj().REACT_APP_FILE_SERVICE_APPKEY;
  const { REACT_APP_FILE_SERVICE_HOST } = getWebEnvObj();
  const fileServiceHost = hostOrigin || REACT_APP_FILE_SERVICE_HOST;
  if (!appKey) {
    throw new Error("缺少配置[appKey]");
  }
  const authInfo: any = {};
  authInfo["x-client"] = (!isFocusEnv() && "WEB") || appRuntimeEnv.registryConfig.client;
  authInfo["x-token"] = info.accessToken;
  authInfo["x-team-id"] = info.teamUserInfo?.teamId || "";
  authInfo["x-gateway-id"] = appRuntimeEnv.gatewayAppId;
  logger.debug("初始化文件上传，", authInfo);
  const clientType = (!isFocusEnv() && "WEB") || authInfo["x-client"];

  let appId = authInfo["x-gateway-id"];
  if (FocusSDK.getDeviceInfo().platform === "web") {
    appId = getWebEnvObj().REACT_APP_WEB_APPID;
  }
  const { pkgVersion } = appRuntimeEnv.registryConfig;
  FileService.uploadService = new UploadFileService({
    appKey: appKey, // 文件服务appkey
    host: fileServiceHost,
    janus: {
      host: appRuntimeEnv.gatewayHost,
      appSecretKey,
      appId, // getAppId() || '', // janus网关appid
      // @ts-ignore
      stage: appRuntimeEnv.gateway.stage || getWebEnvObj().REACT_APP_JANUS_STAGE,
      appVersion: getAppVersion(FocusSDK) || pkgVersion.split("-")[0],
      appKey: appSecretKey,
      clientType,
      hostAppId: (window as any)?._BTS_CONFIG?.REACT_APP_DD_APPID || process.env.REACT_APP_DD_APPID
    },
    auth: {
      // 认证信息
      xToken: authInfo["x-token"], // 用户登录token
      xTeamId: authInfo["x-team-id"], // 用户所在teamId
      xClient: clientType // 客户端类型
    }
  });
};

/**
 * 创建会话上传文件服务
 * @param id
 * @param blob
 * @param options
 */
export const createUploadTask = (
  id: string,
  blob: File,
  options?: Partial<TaskOptions> & { tag?: number }
): Task | undefined => {
  let times: Record<string, number> = {};
  const uploadService = FileService.uploadService;
  console.log("createUploadTask=>", options);
  if (uploadService) {
    const task = uploadService.addTask(blob, {
      fileName: blob.name,
      fileSize: blob.size,
      fileType: blob.type,
      needAuthn: 1,
      ...(options || {})
    } as any);
    times["sta_ts"] = new Date().getTime();
    if (task) {
      // const tasks = FileTaskMap.get(sessionId) || [];
      // tasks.push(task);
      // FileTaskMap.set(sessionId, tasks);
      // if (tasks.length > 10) {
      //     // 上传超过10个文件，最早的文件会被清理
      //     tasks.shift();
      // }
      FocusSDK.sendTrackLog({
        type: "UPDFILE",
        data: {
          sta_ts: times["sta_ts"],
          id: task.id
        }
      });
      task.on("state", async (state1: any) => {
        if (state1.state === "calc") {
          FocusSDK.sendTrackLog({
            type: "UPDFILE",
            data: {
              up1_et: new Date().getTime() - times["sta_ts"],
              id: task.id
            }
          });
          times["up1_et"] = new Date().getTime();
        }
        if (state1.state === "initing") {
          FocusSDK.sendTrackLog({
            type: "UPDFILE",
            data: {
              up2_et: new Date().getTime() - times["up1_et"],
              id: task.id
            }
          });
          times["up2_et"] = new Date().getTime();
        }
        if (state1.state === "completed") {
          FocusSDK.sendTrackLog({
            type: "UPDFILE",
            data: {
              up3_et: new Date().getTime() - times["up2_et"],
              id: task.id,
              end_ts: new Date().getTime(),
              f_t: state1.type,
              f_s: state1.size,
              f_n: state1.name,
              url: state1.downloadUrl,
              md5: state1.md5
            }
          });
        }
      });
      return task;
    }
  }
};

export const getUploadTasks = (sessionId: string): Task[] => {
  return FileTaskMap.get(sessionId) || [];
};

export const clearUploadTasks = (sessionId: string): void => {
  FileTaskMap.delete(sessionId);
};

export const getUploadTask = (sessionId: string, taskId: string): Task | undefined => {
  const tasks = FileTaskMap.get(sessionId) || [];
  const task = tasks.find((t) => t.id === taskId);
  if (task) {
    return task;
  }
};

export const removeUploadTask = (sessionId: string, task: Task | undefined): void => {
  if (!task) {
    return;
  }
  let tasks = FileTaskMap.get(sessionId) || [];
  if (tasks.length > 0) {
    tasks = tasks.filter((item) => item.id !== task.id);
    FileTaskMap.set(sessionId, tasks);
  }
};

// setTimeout(async () => {
//     try {
//         await initFileUploadService();
//     } catch (e) {
//         console.error('初始化文件服务失败 =>', e.message);
//     }
// }, 20);
